import { Params } from '@/utils/types';
import {
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	Pagination,
	PaginationLinkProps
} from '../ui/pagination';
import { mergeQueryParams } from '@/utils/merge-query-params';
import { useLocation, useSearchParams } from 'react-router';
import { RouterLink } from '../link/Link';
import { cn } from '@/lib/utils';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';

export type LpohPaginationProps = {
	currentPage: number;
	totalPages: number;
};
function mergeSearchParams(page: number, searchParams: URLSearchParams) {
	return mergeQueryParams(searchParams, { [Params.page]: page.toString() });
}

type URLPaginationLinkProps = { page: number } & PaginationLinkProps;

function URLPaginationLink({ page, children, ...rest }: URLPaginationLinkProps) {
	const location = useLocation();
	const [searchParams] = useSearchParams();

	return (
		<PaginationLink {...rest} asChild>
			<RouterLink
				to={{
					pathname: location.pathname,
					search: mergeSearchParams(page, searchParams).toString()
				}}
			>
				{children}
			</RouterLink>
		</PaginationLink>
	);
}

function PaginationPrevious({ className, ...props }: React.ComponentProps<typeof URLPaginationLink>) {
	return (
		<URLPaginationLink
			aria-label="Go to previous page"
			size="default"
			className={cn('gap-1 px-2.5 sm:pl-2.5', className)}
			{...props}
		>
			<ChevronLeftIcon />
			<span className="hidden sm:block">Previous</span>
		</URLPaginationLink>
	);
}

function PaginationNext({ className, ...props }: React.ComponentProps<typeof URLPaginationLink>) {
	return (
		<URLPaginationLink
			aria-label="Go to next page"
			size="default"
			className={cn('gap-1 px-2.5 sm:pr-2.5', className)}
			{...props}
		>
			<span className="hidden sm:block">Next</span>
			<ChevronRightIcon />
		</URLPaginationLink>
	);
}

export const LpohPagination = (props: LpohPaginationProps) => {
	const { currentPage, totalPages = 0 } = props;

	const pagesList = Array.from({ length: totalPages }, (_, i) => i + 1);

	const getPagesList = () => {
		if (totalPages < 8) return [...pagesList];

		if (currentPage < 5) return [...pagesList.slice(0, 5), '...', totalPages];

		if (currentPage > totalPages - 4) return [1, '...', ...pagesList.slice(totalPages - 5, totalPages)];

		return [1, '...', ...pagesList.slice(currentPage - 2, currentPage + 1), '...', totalPages];
	};

	const visiblePages = getPagesList();

	return (
		<Pagination className="justify-end">
			<PaginationContent>
				<PaginationItem>
					<PaginationPrevious
						page={currentPage - 1}
						className={currentPage === 1 ? 'pointer-events-none text-gray-500' : ''}
					/>
				</PaginationItem>
				{visiblePages.map((page, i) =>
					typeof page === 'number' ? (
						<PaginationItem key={page}>
							<URLPaginationLink page={page} isActive={currentPage === page}>
								{page}
							</URLPaginationLink>
						</PaginationItem>
					) : (
						<PaginationItem key={page + i}>
							<PaginationEllipsis />
						</PaginationItem>
					)
				)}
				<PaginationItem>
					<PaginationNext
						page={currentPage + 1}
						className={
							currentPage === totalPages || totalPages === 0 ? 'pointer-events-none text-gray-500' : ''
						}
					/>
				</PaginationItem>
			</PaginationContent>
		</Pagination>
	);
};
